import React from 'react';
import { SearchOutlined, UserOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Row,
  Col,
  Table,
  List,
  Popconfirm,
  Button,
  Select,
  message,
  notification,
  Input,
  Tooltip,
} from 'antd';
import { compareIgnoreCase } from '../../../utility/CompareUtils';
import { CreateForm, EditForm } from './index';
import { isSpecificAuthority, isSpecificUser } from '../../../utility/AuthorityUtils';
import {
  USER_DELETE_SUCCESS,
  USER_DELETE_ERROR,
  USER_MASS_CREATE_SUCCESS,
  USER_MASS_CREATE_ERROR,
} from './../../../actions/User';
import { UploadForm } from './../../search';
import './UserManagementForm.less';
import { isIE } from './../../../utility/BrowserUtils';

class UserManagementForm extends React.Component {
  constructor(props) {
    super(props);
    this.searchInputName = React.createRef();
    this.searchInputEmail = React.createRef();
    this.searchInputCompany = React.createRef();
    this.state = {
      filteredInfo: null,
      sortedInfo: null,
      pagination: { defaultPageSize: 50, showSizeChanger: true, size: 'small', position: 'bottom' },
      createUserVisible: false,
      editUserVisible: false,
      userRolesSelect: this.createUserRolesSelect(this.props.userRoles),
      companiesSelect: this.createCompaniesSelect(this.props.companies),
      selectedUser: null,
    };
  }

  /**
   * Action success & errors handling
   * @param prevProps - previous props from earlier state
   */
  componentDidUpdate(prevProps) {
    this.handleActionResponseNotifications(this.props.actionResponse);
  }

  /**
   * Handles notifications for the action response
   * @param actionResponse - the response of the action made
   */
  handleActionResponseNotifications(actionResponse) {
    if (actionResponse === null) {
      return;
    }

    switch (actionResponse.get('type')) {
      case USER_DELETE_ERROR:
        message.error('Der er opstået en fejl, prøv igen.');
        break;

      case USER_DELETE_SUCCESS:
        message.success('Brugeren blev slettet.');
        break;

      case USER_MASS_CREATE_SUCCESS:
        this.handleShowMassCreateUsers();
        break;

      case USER_MASS_CREATE_ERROR:
        message.error('Ingen brugere kunne oprettes, prøv igen.');
        break;

      default:
        break;
    }
    this.props.clearActionResponse();
  }

  /**
   * Deletes a user
   * @param user to be deleted
   */
  handleDelete = (user) => {
    this.props.userDelete(user.id);
  };

  /**
   * Edits a user
   * @param user to be edited
   */
  handleEdit = (user) => {
    this.props.userEdit(user);
  };

  /**
   * Creates a user
   * @param user to be created
   */
  handleCreate = (user) => {
    this.props.userCreate(user);
  };

  /**
   * Shows edit window and selects user to show
   * @param user to be edited in modal window
   */
  handleShowEditUserModal = (user) => {
    this.setState({ selectedUser: user });
    this.setState({ editUserVisible: true });
  };

  /**
   * Hides edit window and deselects user
   */
  handleHideEditUserModal = () => {
    this.setState({ selectedUser: null });
    this.setState({ editUserVisible: false });
  };

  /**
   * Shows create user window
   */
  handleShowCreateUserModal = () => {
    this.setState({ createUserVisible: true });
  };

  /**
   * Hides create user window
   */
  handleHideCreateUserModal = () => {
    this.setState({ createUserVisible: false });
  };

  /**
   * Sets filters and sorters on table.
   * @param pagination on table
   * @param filters on table
   * @param sorter on table
   */
  handleChange = (pagination, filters, sorter) => {
    this.setState({
      filteredInfo: filters,
      sortedInfo: sorter,
    });
  };

  /**
   * Generates a notification if some users could not be created.
   */
  handleShowMassCreateUsers = () => {
    if (this.props.failedCreatedUsers.size < 1) {
      return;
    }

    notification.open({
      duration: 0,
      message: 'Nogle brugere kunne ikke oprettes.',
      description: (
        <Row>
          <Col span={24}>
            <List
              itemLayout={'horizontal'}
              dataSource={this.props.failedCreatedUsers}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta description={item.get('username')} />
                </List.Item>
              )}
            />
          </Col>
        </Row>
      ),
    });
  };

  /**
   * Filters column values based on search input
   * @param confirm - function to confirm search input
   * @returns {Function}
   */
  handleSearch = (confirm) => () => {
    confirm();
  };

  /**
   * Sets new search value on each input made
   * @param setSelectedKeys - search value to update.
   * @returns {function(*): *}
   */
  handleSearchChange = (setSelectedKeys) => (e) => {
    return setSelectedKeys(e.target.value ? [e.target.value] : []);
  };

  /**
   * Resets column filters
   * @param clearFilters - function to reset filters on column
   * @returns {Function}
   */
  handleReset = (clearFilters) => () => {
    clearFilters();
  };

  /**
   * Due to IE focus issue on input as subcomponent, additional focus handling must be made.
   * @param e - e is the event which holds target(input)
   */
  handleLostFocus = (e) => {
    if (isIE) {
      e.target.focus();
      e.target.selectionStart += e.target.value.length;
      e.target.selectionEnd = e.target.value.length;
    }
  };

  /**
   * Creates a search input field to filter with.
   * @param setSelectedKeys - the function to either clear or set value of the field
   * @param selectedKeys - the value of the input.
   * @param confirm - the function to confirm the input as search value
   * @param clearFilters - the function to clear filters
   * @param inputRef - the element as ref
   * @param placeholder - the placeholder for the input field
   * @returns {*}
   */
  createFilterDropDown = (
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    inputRef,
    placeholder
  ) => {
    return (
      <div className='custom-filter-dropdown'>
        <Input
          ref={(ele) => (inputRef.current = ele)}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={this.handleSearchChange(setSelectedKeys)}
          onPressEnter={this.handleSearch(confirm)}
          onBlur={this.handleLostFocus}
        />
        <Button type='primary' onClick={this.handleSearch(confirm)}>
          Search
        </Button>
        <Button onClick={this.handleReset(clearFilters)}>Reset</Button>
      </div>
    );
  };

  /**
   * Gets tool tip information/layout about csv upload.
   * @returns {*} returns body for tool tip.
   */
  getUploadTooltip() {
    return (
      <Row className={'tooltip-small-text'}>
        <Col>
          <div>
            - Det er kun tilladt at importere .csv filer <br />
            - Fil formatet består af 3 kolonner: <br />
            1. Email-adresse <br />
            2. Navn <br />
            3. Rolle (USER eller SUPERUSER) <br />
            - Alle værdier skal adskilles af et ';' <br />
            (Dette vil automatisk ske opretter man filen gennem Excel)
          </div>
        </Col>
      </Row>
    );
  }

  /**
   * Compares confirm password of edit and create window to first password.
   * @param rule of the field
   * @param value is the value of the field
   * @param callback is weather the validation fails or not.
   * @param form is the window modal form
   */
  compareToFirstPassword = (rule, value, callback, form) => {
    if (value && value !== form.getFieldValue('password')) {
      callback('De to passwords er ikke identiske');
    } else {
      callback();
    }
  };

  /**
   * Validates first password of edit and create window to confirm password.
   * @param rule of the field
   * @param value is the value of the field
   * @param callback is weather the validation fails or not.
   * @param component is the component that holds the window.
   */
  validateToNextPassword = (rule, value, callback, component) => {
    if (value && component.state.confirmDirty) {
      // if (value) {
      component.props.form.validateFields(['confirm'], { force: true });
    }
    callback();
  };

  /**
   * Tells weather or not the passwords are identical
   * @param e is the object that is interacted with
   * @param component is the form where the object clicked came from.
   */
  handleConfirmBlur = (e, component) => {
    const value = e.target.value;
    component.setState({ confirmDirty: component.state.confirmDirty || !!value });
  };

  /**
   * Gets the company id from the name of the company.
   * @param companyName is the name of the company
   * @returns {*} the id of the company
   */
  getCompanyIdFromCompanyName = (companyName) => {
    if (this.props.isAdmin) {
      for (let i = 0; i < this.props.companies.size; i++) {
        if (companyName === this.props.companies.get(i).get('name')) {
          return this.props.companies.get(i).get('id');
        }
      }
    } else {
      return this.props.user.getIn(['company', 'id']);
    }
    return -1;
  };

  /**
   * Sends an advanced search query to backend based on results from file that was uploaded.
   * @param input the query to the backend.
   */
  handleFileUserCreation = (input) => {
    const splitRegex = /[\r\n]+/;
    const inputArray = input.split(splitRegex);
    let inputObjs = [];
    inputArray.forEach((inputLine) => inputObjs.push(this.handleSplitCsvLine(inputLine)));
    this.props.userMassCreate(inputObjs);
  };

  /**
   * Creates an object based on a line from a csv file.
   * @param inputLine is the line from the csv file.
   * @returns {*} the object that is to be inserted in to a query.
   */
  handleSplitCsvLine(inputLine) {
    const lineArray = inputLine.split(';');
    if (lineArray.length !== 3) {
      return null;
    }

    return {
      username: lineArray[0],
      name: lineArray[1],
      authority: lineArray[2],
      companyId: this.props.user.getIn(['company', 'id']),
    };
  }

  /**
   * Filters the user roles relevant to manage for the current user logged in.
   * @param userRoles is a list of user roles the current user can use.
   * @returns {*} a list of user roles
   */
  createUserRolesSelect = (userRoles) => {
    if (!isSpecificUser(this.props.user, ['ADMIN'])) {
      userRoles = userRoles.filter((value) => value !== 'ADMIN');
    }
    return userRoles.toJS().map((roles) => {
      return (
        <Select.Option key={roles} value={roles}>
          {roles}
        </Select.Option>
      );
    });
  };

  /**
   * Sets if a column should have a filter function or not.
   * @param userRoles to filter on
   * @returns {*} filters
   */
  createUserRolesFilters = (userRoles) => {
    if (userRoles.size < 2) {
      return null;
    }

    const filters = [];
    for (let i = 0; i < userRoles.size; i++) {
      const userRoleValue = userRoles.get(i);
      filters.push({
        text: userRoleValue,
        value: userRoleValue,
      });
    }
    return filters;
  };

  /**
   * Filters the companies relevant to manage for the current user logged in.
   * @param companies is a list of companies the current user can use.
   * @returns {*} a list of companies
   */
  createCompaniesSelect = (companies) => {
    return companies.toJS().map((company) => {
      return (
        <Select.Option key={company.id} value={company.id}>
          {company.name}
        </Select.Option>
      );
    });
  };

  /**
   * Sets if a column should have a filter function or not.
   * @param companies to filter on
   * @returns {*} filters
   */
  createCompaniesFilters = (companies) => {
    if (companies.size < 2) {
      return null;
    }

    const filters = [];
    for (let i = 0; i < companies.size; i++) {
      const nameValue = companies.get(i).get('name');
      filters.push({
        text: nameValue,
        value: nameValue,
      });
    }
    return filters;
  };

  /**
   * Sees if a user is adequate to edit and delete other users
   * @param user is the user to be handled
   * @param record is the row in the table
   * @returns {boolean} is whether or not this is allowed for current user.
   */
  allowEditAndDeleteOnRow(user, record) {
    if (isSpecificUser(user, ['ADMIN'])) {
      return true;
    }

    if (
      isSpecificAuthority(record.authority, ['USER', 'SUPERUSER']) &&
      isSpecificUser(user, ['SUPERUSER'])
    ) {
      return true;
    }

    return false;
  }

  /**
   * Sees if a user is adequate to edit, but not delete themself.
   * @param user is the user being handled
   * @param record is the row in the table
   * @returns {boolean} is whether or not this is allowed for current user
   */
  allowEditOnRow(user, record) {
    if (isSpecificUser(user, ['ADMIN'])) {
      return true;
    }

    if (
      isSpecificAuthority(record.authority, ['USER', 'SUPERUSER']) &&
      isSpecificUser(user, ['SUPERUSER'])
    ) {
      return true;
    }

    if (isSpecificUser(this.props.user, ['USER'])) {
      return true;
    }

    return false;
  }

  /**
   * Creates columns for table
   * @returns {*[]} columns
   */
  createTableColumns(users) {
    let { sortedInfo, filteredInfo } = this.state;
    sortedInfo = sortedInfo || {};
    filteredInfo = filteredInfo || {};
    return [
      {
        title: 'Navn',
        dataIndex: 'name',
        key: 'name',
        width: '25%',
        sorter: (a, b) => compareIgnoreCase(a.name, b.name),
        sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
        filterDropdown:
          users.size < 2
            ? null
            : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                this.createFilterDropDown(
                  setSelectedKeys,
                  selectedKeys,
                  confirm,
                  clearFilters,
                  this.searchInputName,
                  'Søg navn'
                ),
        filterIcon: (filtered) => (
          <SearchOutlined
            className={
              filtered ? 'custom-filter-dropdown-icon-active' : 'custom-filter-dropdown-icon'
            }
          />
        ),
        onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: (visible) => {
          if (visible) {
            setTimeout(() => {
              this.searchInputName.current.focus();
            });
          }
        },
      },
      {
        title: 'Email',
        dataIndex: 'username',
        key: 'username',
        width: '25%',
        sorter: (a, b) => compareIgnoreCase(a.username, b.username),
        sortOrder: sortedInfo.columnKey === 'username' && sortedInfo.order,
        filterDropdown:
          users.size < 2
            ? null
            : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                this.createFilterDropDown(
                  setSelectedKeys,
                  selectedKeys,
                  confirm,
                  clearFilters,
                  this.searchInputEmail,
                  'Søg email'
                ),
        filterIcon: (filtered) => (
          <SearchOutlined
            className={
              filtered ? 'custom-filter-dropdown-icon-active' : 'custom-filter-dropdown-icon'
            }
          />
        ),
        onFilter: (value, record) => record.username.toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: (visible) => {
          if (visible) {
            setTimeout(() => {
              this.searchInputEmail.current.focus();
            });
          }
        },
      },
      {
        title: 'Selskab',
        dataIndex: ['company', 'name'],
        key: 'companyName',
        width: '25%',
        sorter:
          this.props.companies.size < 2
            ? false
            : (a, b) => compareIgnoreCase(a.company.name, b.company.name),
        sortOrder: sortedInfo.columnKey === 'companyName' && sortedInfo.order,
        filterDropdown:
          users.size < 2
            ? null
            : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                this.createFilterDropDown(
                  setSelectedKeys,
                  selectedKeys,
                  confirm,
                  clearFilters,
                  this.searchInputCompany,
                  'Søg virksomhed'
                ),
        filterIcon: (filtered) => (
          <SearchOutlined
            className={
              filtered ? 'custom-filter-dropdown-icon-active' : 'custom-filter-dropdown-icon'
            }
          />
        ),
        onFilter: (value, record) =>
          record.company.name.toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: (visible) => {
          if (visible) {
            setTimeout(() => {
              this.searchInputCompany.current.focus();
            });
          }
        },
      },
      {
        title: 'Rettigheder',
        dataIndex: 'authority',
        key: 'authority',
        width: '13%',
        filters: this.createUserRolesFilters(this.props.userRoles),
        filteredValue: filteredInfo.authority || null,
        onFilter: (value, record) => record.authority.toString() === value,
        sorter: (a, b) => compareIgnoreCase(a.authority, b.authority),
        sortOrder: sortedInfo.columnKey === 'authority' && sortedInfo.order,
      },
      {
        title: '',
        dataIndex: 'edit',
        key: 'edit',
        width: '10%',
        sorter: false,
        render: (text, record, index) => {
          return this.allowEditOnRow(this.props.user, record) ? (
            <Button type={'primary'} onClick={() => this.handleShowEditUserModal(record)}>
              Rediger
            </Button>
          ) : null;
        },
      },
      {
        title: '',
        dataIndex: 'delete',
        key: 'delete',
        width: '2%',
        sorter: false,
        render: (text, record, index) => {
          return this.allowEditAndDeleteOnRow(this.props.user, record) ? (
            <Popconfirm
              title={'Er du sikker på du vil slette brugeren?'}
              onConfirm={() => this.handleDelete(record)}
            >
              <Button type={'danger'}>X</Button>
            </Popconfirm>
          ) : null;
        },
      },
    ];
  }

  render() {
    return (
      <Row>
        <Col span={24}>
          <Row>
            <Col span={12}>
              <div className={'div-title'}>
                <UserOutlined />
                <label className={'spacing-left'}>Brugerindstillinger</label>
              </div>
            </Col>
            {this.props.isAdmin ? (
              <Col span={12}>
                <Row justify={'end'}>
                  <Col>
                    <div className={'div-tooltip-user'}>
                      <Tooltip
                        title={this.getUploadTooltip()}
                        placement='rightTop'
                        autoAdjustOverflow={true}
                        mouseEnterDelay={0.1}
                      >
                        <InfoCircleOutlined className={'icon-info'} />
                      </Tooltip>
                    </div>
                    <UploadForm
                      className={'float-right'}
                      handleSearch={this.handleFileUserCreation}
                    />
                    <Button type={'primary'} onClick={this.handleShowCreateUserModal}>
                      Opret bruger
                    </Button>
                  </Col>
                </Row>
              </Col>
            ) : null}
          </Row>
          <Row>
            <Col span={24}>
              {this.props.isAdmin ? (
                <Table
                  className={'spacing-top'}
                  columns={this.createTableColumns(this.props.users)}
                  dataSource={this.props.users.toJS()}
                  rowKey={(record) => record.id}
                  onChange={this.handleChange}
                  pagination={this.state.pagination}
                  loading={this.props.isLoading}
                />
              ) : (
                <Table
                  className={'spacing-top'}
                  columns={this.createTableColumns([this.props.user.toJS()])}
                  dataSource={[this.props.user.toJS()]}
                  rowKey={(record) => record.id}
                  onChange={this.handleChange}
                  pagination={this.state.pagination}
                  loading={this.props.isLoading}
                />
              )}
              <CreateForm
                user={this.props.user}
                companies={this.state.companiesSelect}
                userRoles={this.state.userRolesSelect}
                visible={this.state.createUserVisible}
                isLoading={this.props.isLoading}
                clearActionResponse={this.props.clearActionResponse}
                actionResponse={this.props.actionResponse}
                getCompanyIdFromCompanyName={this.getCompanyIdFromCompanyName}
                compareToFirstPassword={this.compareToFirstPassword}
                validateToNextPassword={this.validateToNextPassword}
                handleConfirmBlur={this.handleConfirmBlur}
                onCreate={this.handleCreate}
                onCancel={this.handleHideCreateUserModal}
              />
              {this.state.selectedUser ? (
                <EditForm
                  user={this.state.selectedUser}
                  companies={this.state.companiesSelect}
                  userRoles={this.state.userRolesSelect}
                  visible={this.state.editUserVisible}
                  isLoading={this.props.isLoading}
                  isAdmin={this.props.isAdmin}
                  clearActionResponse={this.props.clearActionResponse}
                  actionResponse={this.props.actionResponse}
                  getCompanyIdFromCompanyName={this.getCompanyIdFromCompanyName}
                  compareToFirstPassword={this.compareToFirstPassword}
                  validateToNextPassword={this.validateToNextPassword}
                  handleConfirmBlur={this.handleConfirmBlur}
                  onEdit={this.handleEdit}
                  onCancel={this.handleHideEditUserModal}
                />
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }
}

export default UserManagementForm;
